/**
 * Provider（03 2 章 app/providers・3.1）：QueryClient・テーマ・通知（notistack）・実行時の設定・日付入力（dayjs・日本語）。
 * TanStack Query の既定（03 3.5）：GET の 5xx・通信エラーだけ 1 回再試行（1 秒後）、4xx は再試行しない、更新系は自動で再試行しない。
 * staleTime は 0（243 N-10：画面を開くたびに読み直す）。
 */
import { CloseIcon } from '../../components/icons/LegacyIcons';
import { CssBaseline, IconButton, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { jaJP } from '@mui/x-date-pickers/locales';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import type { ReactNode } from 'react';
import { isApiError } from '../../api/http-error';
import { RuntimeConfigProvider } from '../config/app-config-context';
import type { RuntimeAppConfig } from '../config/app-config';
import '../../lib/dayjs';
import { APP_THEME } from '../theme';

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 243 N-10（IN-053 ほか 19 件）：一覧・詳細は画面を開くたびにサーバーから読み直す（前は 30 秒：03 3.5 FE-D-033）。
        // 控え（gcTime）は残すので、読み直しの間も前の表示は出る
        staleTime: 0,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failure_count, error) =>
          failure_count < 1 && (!isApiError(error) || error.status >= 500 || error.code === 'NETWORK'),
        retryDelay: 1000,
      },
      mutations: { retry: false },
    },
  });
}

export function AppProviders(props: { config: RuntimeAppConfig; query_client: QueryClient; children: ReactNode }) {
  return (
    <RuntimeConfigProvider config={props.config}>
      <QueryClientProvider client={props.query_client}>
        <ThemeProvider theme={APP_THEME}>
          <CssBaseline />
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ja"
            localeText={jaJP.components.MuiLocalizationProvider.defaultProps.localeText}
          >
            <SnackbarProvider
              maxSnack={3}
              autoHideDuration={4000}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              action={(snackbar_key) => (
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="閉じる"
                  onClick={() => closeSnackbar(snackbar_key)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            >
              {props.children}
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </RuntimeConfigProvider>
  );
}
